import { Injectable } from '@angular/core';
import { EditorConfig, EditorConfigHelperService } from '@fleet-beat/editor';
import { forkJoin, Observable, of, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { EditorItemConfig } from '../models/editor-item-type';
import { EditorColumnConfig } from '../models/editor-column-config.model';
import { SelectOption, TranslateResponseDto, TranslateService } from '@fleet-beat/core';

@Injectable({ providedIn: 'root' })
export class EditorConfigTranslateService {
  constructor(
    private editorConfigHelperService: EditorConfigHelperService,
    private translateService: TranslateService,
  ) {}

  translate(config: EditorConfig): Observable<EditorConfig> {
    const observables$ = config.items.map((item: EditorItemConfig | EditorColumnConfig) => {
      if (this.editorConfigHelperService.isEditorFieldConfig(item)) {
        const translateObservables$: Observable<unknown>[] = [
          this.translateService
            .getTranslateText(item.label)
            .pipe(tap((response: TranslateResponseDto) => (item.label = response.responseData.translatedText))),

          this.translateService
            .getTranslateText(item.placeholder)
            .pipe(tap((response: TranslateResponseDto) => (item.placeholder = response.responseData.translatedText))),
        ];

        const isDataSelectConfig = this.editorConfigHelperService.isDataSelectConfig(item);
        if (isDataSelectConfig) {
          translateObservables$.push(
            forkJoin(item.options.map((option: SelectOption) => this.translateSelectOption(option))),
          );
        }

        return forkJoin(translateObservables$);
      }

      if (this.editorConfigHelperService.isEditorTitleConfig(item)) {
        return this.translateService.getTranslateText(item.title).pipe(
          map((response: TranslateResponseDto) => [response.responseData.translatedText]),
          tap(([title]: string[]) => (item.title = title)),
        );
      }

      if (this.editorConfigHelperService.isEditorSpecialFieldConfig(item)) {
        return this.translate(item.editor);
      }

      return of(null);
    });

    return forkJoin(observables$).pipe(map(() => config));
  }

  translateSelectOption(option: SelectOption): Observable<TranslateResponseDto> {
    return this.translateService
      .getTranslateText(option.label)
      .pipe(tap((response: TranslateResponseDto) => (option.label = response.responseData.translatedText)));
  }
}
